import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Check, ListChecks, X } from 'lucide-react';
import type { ChatSuggestedActivity, ChatSuggestedEvent } from '@/lib/api/chatCreation.service';

interface ActivityReviewCardProps {
  selectedEvents: ChatSuggestedEvent[];
  activitySuggestions: Record<string, ChatSuggestedActivity[]>;
  currentEventIndex?: number;
  selectedActivities?: Record<string, string[]>;
  onConfirm?: (selectedActivities: Record<string, string[]>) => void;
  onPrevious?: () => void;
  onSkip?: () => void;
}

export const ActivityReviewCard: React.FC<ActivityReviewCardProps> = ({
  selectedEvents,
  activitySuggestions,
  currentEventIndex,
  selectedActivities,
  onConfirm,
  onPrevious,
  onSkip,
}) => {
  const safeEventIndex = Math.min(
    Math.max(currentEventIndex ?? 0, 0),
    Math.max(selectedEvents.length - 1, 0),
  );
  const currentEvent = selectedEvents[safeEventIndex];
  const currentActivities = currentEvent ? (activitySuggestions[currentEvent.id] ?? []) : [];

  const initialSelection = useMemo(() => {
    if (currentEvent && selectedActivities && Array.isArray(selectedActivities[currentEvent.id])) {
      return new Set(selectedActivities[currentEvent.id]);
    }
    return new Set(
      currentActivities
        .filter((activity) => activity.selectedByDefault !== false)
        .map((activity) => activity.title),
    );
  }, [currentEvent?.id, currentActivities, selectedActivities]);

  const [selected, setSelected] = useState<Set<string>>(initialSelection);

  useEffect(() => {
    setSelected(initialSelection);
  }, [initialSelection]);

  const dependencyLookup = useMemo(() => {
    const lookup = new Map<string, string>();
    for (const activity of currentActivities) {
      lookup.set(activity.id, activity.title);
    }
    return lookup;
  }, [currentActivities]);

  if (!currentEvent) return null;

  const toggleActivity = (eventId: string, activityTitle: string) => {
    if (eventId !== currentEvent.id) {
      return;
    }

    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(activityTitle)) {
        next.delete(activityTitle);
      } else {
        next.add(activityTitle);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm?.({ [currentEvent.id]: Array.from(selected) });
  };

  const canGoBack = safeEventIndex > 0;
  const totalEvents = selectedEvents.length;
  const confirmLabel = safeEventIndex < totalEvents - 1 ? 'Save and continue' : 'Confirm Activities';

  return (
    <Card className="border-primary/20 bg-primary/5 max-w-2xl w-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-primary" />
          Suggested Activities
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Event {safeEventIndex + 1} of {totalEvents}: {currentEvent.title}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2 rounded-md border bg-background p-3">
          <p className="text-sm font-medium">{currentEvent.title}</p>
          {currentActivities.length > 0 ? (
            currentActivities.map((activity) => {
              const dependencyTitles = (activity.dependsOnActivityIds ?? [])
                .map((dependencyId) => dependencyLookup.get(dependencyId))
                .filter((dependencyTitle): dependencyTitle is string => Boolean(dependencyTitle));

              return (
                <div key={activity.id} className="space-y-1.5 rounded-md border border-border/60 bg-muted/30 p-2.5">
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      aria-label={`Select activity ${activity.title} for ${currentEvent.title}`}
                      checked={selected.has(activity.title)}
                      onChange={() => toggleActivity(currentEvent.id, activity.title)}
                      className="mt-0.5 h-4 w-4 rounded border border-input"
                    />
                    <div>
                      <p className="text-sm">{activity.title}</p>
                      {activity.category && (
                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{activity.category}</p>
                      )}
                    </div>
                  </div>
                  <div className="pl-6 text-xs text-muted-foreground">
                    {dependencyTitles.length > 0 ? (
                      <span>Depends on: {dependencyTitles.join(', ')}</span>
                    ) : (
                      <span>No dependencies listed</span>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-sm text-muted-foreground">
              No activities were suggested for this event. You can continue to the next step.
            </p>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex gap-2 pt-2">
        {onPrevious && canGoBack && (
          <Button size="sm" variant="outline" onClick={onPrevious} className="flex-1">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        )}
        {onSkip && (
          <Button size="sm" variant="outline" onClick={onSkip} className="flex-1">
            <X className="h-4 w-4 mr-1" />
            Skip this event
          </Button>
        )}
        {onConfirm && (
          <Button size="sm" onClick={handleConfirm} className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700">
            {safeEventIndex < totalEvents - 1 ? <ArrowRight className="h-4 w-4 mr-1" /> : <Check className="h-4 w-4 mr-1" />}
            {confirmLabel}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};
